/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as MsDyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import { IHerbalTextBlockVideoProps } from './herbal-text-block-video.props.autogenerated';
import { HerbalVideoPlayerPopup, IHerbalVideoPlayerProps } from './components/herbal-video-player-popup';
import { observer } from 'mobx-react';
import { getTelemetryObject, isMobile, ITelemetryContent, VariantType } from '@msdyn365-commerce-modules/utilities';
import { IPlayerData, IPlayerOptions, IPlayerResources, IVideoMetadata, Player } from '@msdyn365-commerce/components';

export interface IHerbalTextBlockVideoViewProps extends IHerbalTextBlockVideoProps<{}> {
    paragraph?: React.ReactNode;
    videoPlayerOptions?: IHerbalVideoPlayerProps;
    className?: string;
    videoPlayer?: IHerbalVideoPlayerViewProps;
}

export interface IHerbalVideoPlayerViewProps {
    player?: React.ReactNode;
}
/**
 * Cart component state interface.
 */
interface IHerbalTextBlockVideoState {
    showVideoPlayerDialog: boolean;
}

/**
 *
 * HerbalTextBlockVideo component.
 */
@observer
class HerbalTextBlockVideo extends React.PureComponent<IHerbalTextBlockVideoProps<{}>> {
    private readonly telemetryContent: ITelemetryContent;
    public state: IHerbalTextBlockVideoState;
    private readonly videoPlayerPopupId: string = 'herbal-video-player-popup';
    private readonly isMobileDevice: boolean;
    private readonly playerContainer: React.RefObject<Player> = React.createRef<Player>();

    public constructor(props: IHerbalTextBlockVideoProps<{}>) {
        super(props);
        this.isMobileDevice = isMobile({ variant: VariantType.Viewport, context: this.props.context.request }) === 'xs';
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.videoPlayerLinkClick = this.videoPlayerLinkClick.bind(this);
        this.videoPlayerCloseButtonClick = this.videoPlayerCloseButtonClick.bind(this);
        this.state = {
            showVideoPlayerDialog: false
        };
    }

    /**
     * Should component update function.
     * @param nextProps - Next props.
     * @param nextState - Next state.
     * @returns True if component should update, false otherwise.
     */
    public shouldComponentUpdate(nextProps: IHerbalTextBlockVideoProps<{}>, nextState: IHerbalTextBlockVideoState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const { paragraph, className } = this.props.config;
        const options = {
            ...this.props.config,
            isMobileDevice: this.isMobileDevice
        };
        const video: MsDyn365.IVideoData | undefined = this.props.config.video;
        const playerResources: IPlayerResources = {
            playAriaLabel: this.props.resources.videoPlayLabel,
            pauseAriaLabel: this.props.resources.videoPauseLabel,
            playingAriaLabel: this.props.resources.videoPlayingLabel,
            pausedAriaLabel: this.props.resources.videoPausedLabel,
            muteAriaLabel: this.props.resources.videoMuteLabel,
            unMuteAriaLabel: this.props.resources.videoUnMuteLabel,
            fullScreenAriaLabel: this.props.resources.videoFullScreenLabel,
            exitFullScreenAriaLabel: this.props.resources.videoExitFullScreenLabel,
            seekBarLabel: this.props.resources.videoSeekBarLabel,
            videoTimeDurationLabel: this.props.resources.videoVideoTimeDurationLabel,
            closedCaptionLabel: this.props.resources.videoClosedCaptionLabel
        };
        const videoMetaData: IVideoMetadata = {};

        if (video) {
            videoMetaData.videoId = video.id;
            videoMetaData.title = video.title;
            videoMetaData.duration = video.playTime;

            if (video.thumbnail) {
                videoMetaData.posterframeUrl = video.thumbnail.src;
            }

            if (video._links?.binaryReferences) {
                videoMetaData.videoBinaryReferences = video._links.binaryReferences;
            }
        }
        const videoPlayerData: IPlayerData = { options: options as IPlayerOptions, metaData: videoMetaData };

        const text = paragraph && (
            <MsDyn365.RichTextComponent
                text={paragraph}
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );
        const videoPlayerViewProps = {
            player: this.props.config.video && (
                <MsDyn365.Video className='' editProps={{ key: this.props.config.video, requestContext: this.props.context.request }}>
                    <Player
                        ref={this.playerContainer}
                        playerData={videoPlayerData}
                        playerResources={playerResources}
                        telemetryContent={this.telemetryContent}
                        isEditor={this.props.context.request.params.isEditor}
                    />
                </MsDyn365.Video>
            )
        };
        const HerbalTextBlockVideoViewProps = {
            ...this.props,
            paragraph: text,
            videoPlayerOptions: this._getVideoPlayer(videoPlayerViewProps),
            className: classnames('ms-text-block', className),
            videoPlayer: {
                ...videoPlayerViewProps
            }
        };
        return this.props.renderView(HerbalTextBlockVideoViewProps) as React.ReactElement;
    }

    public handleTextChange = (event: MsDyn365.ContentEditableEvent) => (this.props.config.paragraph = event.target.value);

    /**
     * Get video player.
     * @returns React node with the video player.
     */
    private _getVideoPlayer(videoPlayer: IHerbalVideoPlayerViewProps): React.ReactNode {
        return (
            videoPlayer.player &&
            HerbalVideoPlayerPopup({
                id: this.videoPlayerPopupId,
                showVideoPopup: this.state.showVideoPlayerDialog,
                onClick: this.videoPlayerLinkClick,
                onCloseButtonClick: this.videoPlayerCloseButtonClick,
                videoPlayer: videoPlayer,
                resources: {
                    videoPlayerCloseButtonAriaLabel: this.props.resources.videoPlayerCloseButtonAriaLabel
                },
                config: {
                    videoPlayerLinkText: this.props.config.buttonLabel ?? ''
                }
            })
        );
    }

    /**
     * Video player click event handler.
     * @param event - The mouse event.
     */
    private readonly videoPlayerLinkClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        this.setState({
            showVideoPlayerDialog: !this.state.showVideoPlayerDialog
        });
    };

    /**
     * Video player close button click.
     */
    private readonly videoPlayerCloseButtonClick = () => {
        this._hideVideoPlayerDialog();
    };

    /**
     * Hide video player dialog.
     */
    private _hideVideoPlayerDialog(): void {
        // Pause the video first.
        const elPlayer = this.playerContainer.current! as Player;
        elPlayer && elPlayer.pause();
        // Hide popup.
        this.setState({
            showVideoPlayerDialog: false
        });
    }
}
export default HerbalTextBlockVideo;
